import React, { useState, useEffect, useContext, useRef } from 'react'
import { useLocation, useRouteMatch, matchPath } from 'react-router-dom'
import { Alert } from 'react-bootstrap'
import { WebSocketContext } from 'contexts/WebSocketProvider'
import { useLockEdit } from 'contexts/PatientLockEditProvider'
import useAuth from 'hooks/useAuth'
import Swal from 'sweetalert2'
import HoldBillDetails from './HoldBillDetails'

const NotificationBar = ({ channelName = '', eventName = '' }) => {
  const { isLoggedIn, user, config, perspective } = useAuth()
  const {
    connected,
    currentChannel,
    socket,
    enableDebugLog,
    subscribe,
    unsubscribe,
    bindBroadcast,
    unbindBroadcast,
    broadcastMessage,
    pushMessage,
  } = useContext(WebSocketContext)
  const {
    isLocked,
    setLockStatus,
    isLockedFinance,
    setLockFinanceStatus,
    eventMessage,
    setEventMessage,
    isLockedMedOPD,
    medOpdMessage,
  } = useLockEdit()
  const [isConcurrentLock, setIsConcurrentLock] = useState(false)
  const [appearance, setAppearance] = useState('warning')
  const [event, setEvent] = useState(null)
  //const [activeChannel,setActiveChannel] = useState('')
  const activeChannelRef = useRef('')
  const patientLockChannelRef = useRef('')
  const holdBillEventVersionRef = useRef(0)
  let notifyRouteMatch = useRouteMatch(
    '/patient-file/:section/:module/:submodule/'
  )
  let notifyRouteMatch2 = useRouteMatch(
    '/patient-file/:section/:module/:submodule/:detail'
  )
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const hn = queryParams.get('hn')
  const vn = queryParams.get('vn')
  const an = queryParams.get('an')
  const placeCode = queryParams.get('placeCode')

  useEffect(() => {
    if (!connected) {
      return
    }
    if (activeChannelRef.current !== '') {
      unsubscribe(activeChannelRef.current)
    }

    //console.log('match',notifyRouteMatch,notifyRouteMatch2)
    let url = notifyRouteMatch2
      ? notifyRouteMatch2.url
      : notifyRouteMatch
      ? notifyRouteMatch.url
      : ''

    if (url != '') {
      const newChannelName =
        channelName +
        '::' +
        url.replace(/^\//, '_').replace(/\//g, '_') +
        '::' +
        vn +
        (placeCode ? `::${placeCode}` : '')
      //eventName = 'enter-page'
      const eventName = 'socket_internal::subscription_changed'
      enableDebugLog(process.env.NODE_ENV == 'development')
      const channel = subscribe(newChannelName)
      channel.bind(eventName, onEventChange)
      const eventName2 = 'socket_internal::subscription_succeeded'
      channel.bind(eventName2, onEventChange)

      //setActiveChannel(newChannelName)
      activeChannelRef.current = newChannelName
    }
    return () => {
      if (
        activeChannelRef.current !== '' &&
        activeChannelRef.current !== 'nav'
      ) {
        unsubscribe(activeChannelRef.current)
      }
    }
  }, [connected, location])

  useEffect(() => {
    const isPatientFileOpen = !!(notifyRouteMatch || notifyRouteMatch2)
    if (!connected || !vn || !isPatientFileOpen) {
      return
    }

    const room = 'patient-lock-edit-' + vn
    const channel = subscribe(room)
    channel.bind('patient-lock-edit-changed', onPatientLockEditChanged)
    patientLockChannelRef.current = room

    return () => {
      unsubscribe(room)
      if (patientLockChannelRef.current === room) {
        patientLockChannelRef.current = ''
      }
    }
  }, [connected, location])

  const onPatientLockEditChanged = ({ data = {} }) => {
    if (String(data.vn || '') !== String(vn || '')) return

    const locked = Number(data.isLocked) === 1
    if (data.moduleName === 'ALL') {
      setLockStatus(locked)
      Swal.fire({
        title: locked ? 'ระบบ Lock การแก้ไขข้อมูลแล้ว' : 'ปลด Lock การแก้ไขข้อมูลแล้ว',
        text: locked
          ? 'ไม่สามารถแก้ไขหรือบันทึกข้อมูลผู้ป่วยใน Visit นี้ได้'
          : 'สามารถแก้ไขข้อมูลผู้ป่วยได้ตามสิทธิ์ตามปกติ',
        icon: locked ? 'warning' : 'info',
        confirmButtonText: 'รับทราบ',
      })
      return
    }
    if (data.moduleName !== 'HOLD_BILL') return

    holdBillEventVersionRef.current += 1
    setLockFinanceStatus(locked)
    setEventMessage(
      locked ? (
        <>
          รายการนี้ได้ทำการ{' '}
          <strong className="text-c-red">Hold Bill</strong>{' '}
          จึงไม่สามารถแก้ไขค่าบริการได้
          <HoldBillDetails vn={vn} refreshKey={holdBillEventVersionRef.current} />
        </>
      ) : undefined
    )

    Swal.fire({
      title: locked ? 'ระบบ Lock ค่าใช้จ่ายแล้ว' : 'ปลด Hold Bill แล้ว',
      text: locked
        ? 'ไม่สามารถเพิ่ม แก้ไข หรือยกเลิกรายการค่าใช้จ่ายได้ แต่ยังบันทึกข้อมูลการรักษาอื่นได้ตามปกติ'
        : 'สามารถเพิ่มและแก้ไขรายการค่าใช้จ่ายได้ตามปกติ',
      icon: locked ? 'warning' : 'info',
      confirmButtonText: 'รับทราบ',
    })
  }

  const onEventChange = (event) => {
    const activeChannel = activeChannelRef.current
    console.log('onEventChange', activeChannel, event)

    //debugger
    if (
      event?.channel === activeChannel &&
      event?.data?.clients &&
      event?.data?.clients.length > 1
    ) {
      const counts = event.data.clients.reduce((acc, client) => {
        // Increment the count for this userId, if the client is not the current socket
        if (client.socketId !== socket?.id) {
          acc[client.userId] = (acc[client.userId] || 0) + 1
        }
        return acc
      }, {})

      //const namesList = event.data.clients.filter(client=>client.clientId!==socket?.id).map(client => client.name).join(', ');
      const namesList = event.data.clients
        .filter((client) => client.socketId !== socket?.id)
        .map((client) => {
          const name = client.name
          const count = counts[client.userId]
          if (count > 1) return `${name} (${count})`
          else return name
        })

      // Remove duplicates from namesList and join names with counts
      const uniqueNamesList = Array.from(new Set(namesList)).join(', ')

      setEvent({
        message: `Please be aware that user ${uniqueNamesList} and me look the same file `,
      })

      //If not the first in Room then Lock
      if (event.data.clients[0].socketId !== socket?.id) {
        checkConcurrentLock(uniqueNamesList)
      }
    } else {
      setEvent(null)
      setIsConcurrentLock(false)
    }
  }

  const onDismiss = () => {}

  const checkConcurrentLock = (uniqueNamesList) => {
    const find = config.find(
      ({ configCode }) => configCode === 'endpoint_concurrent_lock'
    )
    if (find) {
      const hash = window.location.hash // Output: '#/patient-file/ipd/display/form/form-screening?hn=...'
      const hashPath = hash.split('?')[0].replace('#', '')
      find.jsonData.forEach((item) => {
        if (item.path) {
          const routeMatch = matchPath(hashPath, {
            path: item.path,
            exact: true, // Optional: set to true if the path must match exactly
            strict: false,
          })
          if (routeMatch) {
            setEvent({
              message: `Lock edit cause user ${uniqueNamesList} and me look the same file `,
            })
            setIsConcurrentLock(true)
            Swal.fire({
              title: 'Lock Edit',
              text: `User ${uniqueNamesList} and me look the same file `,
              icon: 'warning',
              showCancelButton: false,
              confirmButtonText: 'Accept it',
            }).then((result) => {
              if (result.isConfirmed) {
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.fire('Cancelled', 'Your file is safe :)', 'info')
              }
            })
          }
        }
      })
    }
  }

  const alerts = []

  if (isConcurrentLock) {
    alerts.push(
      <Alert key="concurrent" variant={appearance} onClose={onDismiss}>
        <i className="fas fa-exclamation-triangle m-r-10"></i>
        Lock Edit Mode {event && ' : ' + event.message}
      </Alert>
    )
  }

  if (isLocked) {
    alerts.push(
      <Alert key="readonly" variant={appearance} onClose={onDismiss}>
        <i className="fas fa-exclamation-triangle m-r-10"></i>
        Readonly Mode {event && ' : ' + event.message}
      </Alert>
    )
  }

  if (isLockedFinance) {
    alerts.push(
      <Alert key="finance" variant={appearance} onClose={onDismiss}>
        <strong>Finance Lock : </strong> {eventMessage || null}
      </Alert>
    )
  }

  if (isLockedMedOPD) {
    alerts.push(
      <Alert key="medOpd" variant={appearance} onClose={onDismiss}>
        <strong>Medication Lock : </strong> {medOpdMessage || null}
      </Alert>
    )
  }

  if (event && !isConcurrentLock && !isLocked) {
    alerts.push(
      <Alert key="event" variant={appearance} onClose={onDismiss}>
        <i className="fas fa-exclamation-triangle m-r-10"></i>
        {event.message}
      </Alert>
    )
  }

  return alerts.length > 0 ? <div>{alerts}</div> : null
}

export default NotificationBar
